import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { COPILOT_CONFIG } from './config';
import { CopilotMessage, PageContext } from './types';

const STORAGE_KEY = 'copilotHistory';

const loadStored = (): CopilotMessage[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Self-contained axios instance pointed at the agent_manager service.
// Deliberately separate from the app's useAxios so nothing existing is touched.
// withCredentials sends the session cookie when same-origin (real per-user RBAC);
// AGENT_BEARER_TOKEN is the explicit per-user lever for local cross-origin demos.
const client = axios.create({
  baseURL: COPILOT_CONFIG.AGENT_BASE_URL,
  withCredentials: true,
  headers: COPILOT_CONFIG.AGENT_BEARER_TOKEN
    ? { Authorization: `Bearer ${COPILOT_CONFIG.AGENT_BEARER_TOKEN}` }
    : {},
});

export interface AgentIdentity {
  role: string;
  firstName: string;
}

export const useAgent = () => {
  const [messages, setMessages] = useState<CopilotMessage[]>(loadStored);
  const [loading, setLoading] = useState(false);
  const [identity, setIdentity] = useState<AgentIdentity | null>(null);

  // Persist the conversation so it survives reloads.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* ignore quota / serialization errors */
    }
  }, [messages]);

  const reset = useCallback(() => {
    setMessages([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Resolve who the Copilot is acting as (makes per-user RBAC visible in the header).
  const loadIdentity = useCallback(async () => {
    try {
      const res = await client.get('/agent/whoami');
      const m = res?.data?.messages?.[0];
      if (m) setIdentity({ role: m.role, firstName: m.first_name });
    } catch {
      setIdentity(null);
    }
  }, []);

  const send = useCallback(
    async (text: string, context?: PageContext | null) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      setLoading(true);

      try {
        const res = await client.post(`/agent/${COPILOT_CONFIG.AGENT_ID}/chat`, {
          message: trimmed,
          history,
          context: context || undefined,
          // Send the user's browser timezone so Clara's dashboard counts use the same window
          // as the app dashboard (which passes this same timezone).
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        const payload = res?.data?.messages?.[0];
        if (payload) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: payload.reply || '(no answer)',
              citations: payload.citations || [],
              toolActivity: payload.tool_activity || [],
              report: payload.report || null,
            },
          ]);
        } else {
          const err = res?.data?.errors?.[0] || 'No response from the assistant.';
          setMessages((prev) => [...prev, { role: 'assistant', content: err, isError: true }]);
        }
      } catch (e: any) {
        const detail = e?.response?.data?.errors?.[0] || e?.message || 'Request to the assistant failed.';
        setMessages((prev) => [...prev, { role: 'assistant', content: detail, isError: true }]);
      } finally {
        setLoading(false);
      }
    },
    [messages, loading],
  );

  return {
    messages, loading, send, identity, loadIdentity, reset,
  };
};
